import { useMemo, useState } from "react";
import { Droplet, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * LowHpPotionNudge (iter67.6) — flashes a pulsing "DRINK" button when the
 * player's HP ratio drops under 25% AND they still hold a healing potion.
 * Saves safety-net players from forgetting the inventory button in panic.
 *
 * Prefers the WORST (lowest-heal) potion first so premium potions are
 * conserved for truly dire moments — but if only major/elixir are available,
 * it uses those.
 */
const POTION_PRIORITY = ["pot_minor_heal", "pot_major_heal", "pot_elixir"];
const HEAL_VALUE = { pot_minor_heal: 30, pot_major_heal: 70, pot_elixir: 999 };

export default function LowHpPotionNudge({ run, onUsed }) {
  const [busy, setBusy] = useState(false);

  const bestPotion = useMemo(() => {
    const inv = run?.inventory || {};
    for (const k of POTION_PRIORITY) {
      if ((inv[k] || 0) > 0) return k;
    }
    return null;
  }, [run?.inventory]);

  const hp = run?.health || 0;
  const max = run?.max_health || 1;
  const isLow = hp > 0 && hp / max < 0.25;
  const show = isLow && bestPotion && !run?.is_dead && !run?.is_victory;

  if (!show) return null;

  const heal = HEAL_VALUE[bestPotion] || 30;
  const label = bestPotion === "pot_elixir" ? "ELIXIR" : (bestPotion === "pot_major_heal" ? "MAJOR" : "MINOR");

  const drink = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post("/inventory/use", { item_id: bestPotion });
      toast.success(`Drank ${label} — restored ${res.hp_delta || heal} HP.`);
      onUsed && onUsed(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not drink.");
    }
    setBusy(false);
  };

  return (
    <button
      type="button"
      onClick={drink}
      disabled={busy}
      data-testid="low-hp-potion-nudge"
      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-sm font-heading tracking-widest text-sm transition-all disabled:opacity-60 disabled:cursor-wait low-hp-nudge-pulse"
      style={{
        background: "linear-gradient(90deg, rgba(192,57,43,0.35), rgba(226,88,34,0.45), rgba(192,57,43,0.35))",
        border: "1px solid rgba(255,107,58,0.85)",
        color: "#ffd8b8",
      }}
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : <Droplet size={14} />}
      DRINK {label} · HEAL +{heal === 999 ? "FULL" : heal}
    </button>
  );
}
